<template>
  <v-container class="px-8">
    <div class="py-3 pl-2 flex-grow-0 flex-shrink-0">
      <div class="d-flex flex-row align-end">
        <div class="text-h4 font-weight-medium">
          {{ $t("navbar.menu.settings") }}
        </div>
        <v-dialog v-model="deleteDialog" width="500">
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="error mx-2" small dark v-bind="attrs" v-on="on">
              <v-icon small>{{ archived ? "mdi-delete" : "mdi-archive" }}</v-icon>
              {{ archived ? "Delete Trip" : "Archive Trip" }}
            </v-btn>
          </template>

          <v-card>
            <v-card-title class="text-h5 primary white--text">
              {{ archived ? "Delete Trip" : "Archive Trip" }}
            </v-card-title>

            <v-card-text>
              Are you sure you want to {{ archived ? "delete" : "archive" }} this trip?
            </v-card-text>
            <v-card-text>
              {{ archived ? "" : "- Archive: Trip will be remove from trips lists" }}
              <br />- Delete: Trip will be permanently deleted
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions>
              <v-btn color="error" @click="deleteTrip"> Delete </v-btn>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="deleteDialog = false"> Cancel </v-btn>
              <v-btn v-show="!archived" color="error" outlined @click="archiveTrip">
                Archive
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-chip v-if="archived" class="ml-2 warning"> Archived </v-chip>
        <v-chip v-if="deleted" class="ml-2 error"> Deleted </v-chip>
      </div>
    </div>
    <v-card max-width="70vw">
      <!-- <v-card-title>Trip Settings</v-card-title> -->
      <v-card-text>
        <v-row>
          <v-col cols="6">
            <v-card max-width="350" class="mx-auto">
              <v-img
                class="white--text"
                width="400"
                aspect-ratio="1.66"
                :src="imageSrc || imageUrl"
              />

              <v-card-text style="position: relative">
                <input
                  type="file"
                  style="display: none"
                  ref="image"
                  accept="image/*"
                  @change="onFilePicked"
                />
                <small>{{ $t("base.DragAndDrop.small") }}</small>
                <v-btn color="primary" dark absolute top right fab @click="$refs.image.click()">
                  <v-icon>mdi-camera</v-icon>
                </v-btn>

                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="tripName"
                      class="align-end fill-height mx-3 headline"
                      append-icon="mdi-pencil"
                      :label="$t('NewTrip.tripName')"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      v-model="tripDescription"
                      :label="$t('common.description')"
                      rows="2"
                      counter
                      maxlength="120"
                      class="mx-3"
                    ></v-textarea>
                  </v-col>

                  <v-col cols="12" sm="6">
                    <v-menu
                      v-model="menuStartDate"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="startDate"
                          :label="$t('common.startDate')"
                          prepend-icon="mdi-calendar-arrow-left"
                          readonly
                          :hint="$t('common.startDate')"
                          persistent-hint
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="startDate"
                        @input="menuStartDate = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-menu
                      v-model="menuEndDate"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="endDate"
                          :label="$t('common.endDate')"
                          prepend-icon="mdi-calendar-arrow-right"
                          readonly
                          :hint="$t('common.endDate')"
                          persistent-hint
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker v-model="endDate" @input="menuEndDate = false"></v-date-picker>
                    </v-menu>
                  </v-col>

                  <v-col cols="12">
                    <v-select
                      v-model="leadContact"
                      class="mx-3"
                      :hint="this.$t('settings.leadContactHint')"
                      :items="users"
                      item-text="name"
                      item-value="id"
                      :label="this.$t('settings.leadContactLabel')"
                      persistent-hint
                      single-line
                    ></v-select>
                  </v-col>
                  <v-col>
                    <h4>{{ $t("common.privacyLabel") }}</h4>
                    <v-radio-group
                      dense
                      class="mt-2"
                      persistent-hint
                      row
                      v-model="tripPrivacy"
                      :hint="
                        tripPrivacy == 'public'
                          ? this.$t('settings.tripPrivacyT')
                          : this.$t('settings.tripPrivacyF')
                      "
                    >
                      <v-radio :label="this.$t('common.public')" value="public"></v-radio>
                      <v-radio
                        :label="this.$t('settings.radioStatusPrivate')"
                        value="private"
                      ></v-radio>
                    </v-radio-group>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col>
            <v-card>
              <v-card-title>{{ $t("settings.customization") }}</v-card-title>
              <v-card-text>
                <h4 class>{{ $t("navbar.menu.itinerary") }}</h4>
                <v-switch
                  color="primary"
                  v-model="itineraryNotifications"
                  :label="$t('common.notifications')"
                  :hint="$t('settings.notificationsHint')"
                  persistent-hint
                ></v-switch>
                <v-switch
                  color="primary"
                  v-model="birthdays"
                  :label="$t('settings.birthdaysLabel')"
                  :hint="$t('settings.birthdaysHint')"
                  persistent-hint
                ></v-switch>
                <v-switch
                  v-if="isJTB"
                  color="primary"
                  v-model="audioguides"
                  label="Display audioguides"
                  hint="Display Multimedia Guides tab in menu"
                  persistent-hint
                ></v-switch>
                <v-switch
                  v-if="isJTB"
                  color="primary"
                  v-model="showContacts"
                  label="Display contact list"
                  hint="Display full contact list for non admins"
                  persistent-hint
                ></v-switch>
                <v-switch
                  color="primary"
                  v-model="publicItinerary"
                  :hint="$t('settings.publicItineraryHint')"
                  persistent-hint
                >
                  <template v-slot:label>
                    {{ $t("settings.publicItineraryLabel") }}
                    <v-btn
                      v-if="publicItinerary"
                      class="pt-1"
                      color="primary"
                      target="_blank"
                      :href="`https://itinerary.triploop.com/?tripId=${tripId}`"
                      icon
                      @click.stop
                    >
                      <v-icon small>mdi-open-in-new</v-icon>
                    </v-btn>
                  </template>
                </v-switch>

                <h4 class="mt-4">Chat</h4>
                <v-select
                  v-model="chat"
                  class="mx-3"
                  :hint="chatHint[chat]"
                  :items="chatOptions"
                  item-text="name"
                  item-value="id"
                  :label="$t('settings.chatLabel')"
                  persistent-hint
                  single-line
                ></v-select>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn :loading="saving" class="primary px-8" @click="saveData()">{{
          $t("common.save")
        }}</v-btn>
      </v-card-actions>
    </v-card>
    <v-snackbar v-model="snackbar">
      {{ $t("common.tripSuccess") }}
      <v-btn color="primary" text @click="snackbar = false">{{ $t("common.close") }}</v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import { format, parseISO, endOfDay } from "date-fns";
import firestore from "@/firebase/firestore";
import storage from "@/firebase/storage";
import resizeImage from "@/plugins/image-resize.js";
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";
import { uuid } from "vue-uuid";

export default {
  name: "Settings",
  components: {},
  data() {
    return {
      tripName: null,
      tripDescription: null,
      startDate: null,
      menuStartDate: false,
      endDate: null,
      menuEndDate: false,
      leadContact: null,
      itineraryNotifications: false,
      birthdays: true,
      imageSrc: null,
      file: null,
      saving: false,
      snackbar: false,
      tripPrivacy: null,
      audioguides: false,
      showContacts: true,
      deleted: false,
      archived: false,
      deleteDialog: false,
      publicItinerary: false,

      chatOptions: [
        {
          id: 0,
          name: this.$t("common.privacy.everyone.text"),
        },
        {
          id: 1,
          name: this.$t("company.tabs.administrators.title"),
        },
        {
          id: 2,
          name: this.$t("settings.chatOptions.includeAdmins"),
        },
      ],
      chatHint: this.$i18n.t("settings.chatHint"),
      chat: null,
    };
  },
  methods: {
    async archiveTrip() {
      const { trip } = this.$store.state.trip;
      await firestore.updateTrip(trip.id, { archived: true });
      this.archived = true;
      this.deleteDialog = false;
    },
    async deleteTrip() {
      const { trip } = this.$store.state.trip;
      await firestore.updateTrip(trip.id, { deleted: true });
      this.deleted = true;
      this.deleteDialog = false;
    },
    saveData() {
      this.saving = true;
      const data = {
        name: this.tripName,
        description: this.tripDescription,
        start_date: zonedTimeToUtc(parseISO(this.startDate), "utc"),
        end_date: zonedTimeToUtc(endOfDay(parseISO(this.endDate)), "utc"),
        contact: this.leadContact,
        activity_notifications: this.itineraryNotifications,
        chat_settings: this.chat,
        hide_birthdays: !this.birthdays,
        privacy: this.tripPrivacy,
        deleted: this.deleted,
        archived: this.archived,
        public_itinerary: this.publicItinerary,
      };
      if (this.isJTB) {
        data.audio_guides = this.audioguides;
        data.show_contacts = this.showContacts;
      }
      const { trip } = this.$store.state.trip;

      firestore.updateTrip(trip.id, data);

      if (this.file) {
        const name = `${uuid.v4()}.${this.file.name.split(".").pop().toLowerCase()}`;
        const filePath = `groups/${trip.id}/images/${name}`;
        storage.uploadFile(this.file, filePath).then(() => {
          firestore.updateTrip(trip.id, { "files.header": filePath });
          this.saving = false;
          this.snackbar = true;
        });
      } else {
        console.log("no image, that was fast!");
        this.saving = false;
        this.snackbar = true;
      }
    },

    async onFilePicked(e) {
      const { files } = e.target;
      if (files && files[0] !== undefined) {
        const resizedImageFile = await resizeImage({ file: files[0] });

        const fr = new FileReader();
        fr.readAsDataURL(files[0]);
        fr.addEventListener("load", () => {
          this.file = resizedImageFile;
          this.imageSrc = fr.result;
        });
      }
    },
  },
  computed: {
    users() {
      return this.$store.getters.allUsers;
    },
    tripId() {
      return this.$store.state.trip.trip.id;
    },
    imageUrl() {
      const { trip } = this.$store.state.trip;
      const src = this.$store.state.imageUrls[trip.id];
      if (src && src.includes("/")) {
        return src;
      }
      const { header } = this.$store.state.trip.trip.files || {};
      if (!header) {
        return require("@/assets/trip.jpg");
      }
      const cityImages = {};
      Object.values(this.$store.state.trip.cities).forEach((city) => {
        const imageUrl = city.image;
        let imageName;

        if (imageUrl.includes("wikimedia")) {
          imageName = `${city.wiki_code}.${imageUrl.split(/[#?]/)[0].split(".").pop().trim()}`;
        } else {
          imageName = imageUrl.substring(imageUrl.lastIndexOf("/") + 1);
        }
        cityImages[imageName] = imageUrl;
      });
      if (cityImages[header] != null) {
        return cityImages[header];
      }
      // eslint-disable-next-line import/no-dynamic-require
      return require(`@/assets/img/${header}.jpg`);
    },
    isJTB() {
      const { company: companyId } = this.$store.state.trip.trip;
      return companyId === "R1XBW59p2FFxo5WudH3H" || companyId === "I6Y9GSy3nJifTu0Eg6V7";
    },
  },
  created() {
    const { trip } = this.$store.state.trip;
    if (trip) {
      this.tripName = trip.name;
      this.tripDescription = trip.description || "";
      this.startDate = format(utcToZonedTime(trip.start_date.toDate(), "utc"), "yyyy-MM-dd");
      this.endDate = format(utcToZonedTime(trip.end_date.toDate(), "utc"), "yyyy-MM-dd");
      this.leadContact = trip.contact;
      this.itineraryNotifications = trip.activity_notifications;
      this.chat = trip.chat_settings != null ? trip.chat_settings : 0;
      this.birthdays = !trip.hide_birthdays;
      this.tripPrivacy = trip.privacy;
      this.audioguides = trip.audio_guides ?? false;
      this.showContacts = trip.show_contacts == null ? true : trip.show_contacts;
      this.deleted = trip.deleted || false;
      this.archived = trip.archived || false;
      this.publicItinerary = trip.public_itinerary || false;
    }
  },
};
</script>
